import React from 'react';
import { motion } from 'framer-motion';
import { C } from '../../lib/palette';

// An original companion creature — "Pip". Rounded, friendly, a little
// mischievous; expressive through eyes and posture. Five stages of structural
// change (new features each stage), not just scaling. Original silhouette and
// coloring — borrowed from nothing.
const BODY = '#53A185';
const EDGE = '#3B7E67';
const BELLY = '#DCF0E6';
const FOOT = '#3B7E67';
const INK = '#243029';
const CHEEK = '#E7A6B7';
const SPROUT = '#62B497';
const SPROUT_HI = '#8FD3B8';

function HeadLeaf({ x, y, rot, len = 16, fill = SPROUT }) {
  return (
    <path
      d={`M${x},${y} q -7,-5 0,-${len} q 7,${Math.round(len * 0.62)} 0,${len} z`}
      transform={`rotate(${rot} ${x} ${y})`}
      fill={fill}
      stroke={EDGE}
      strokeOpacity="0.15"
    />
  );
}

export default function CreatureCompanion({ stage = 1, dormant = false, className = '' }) {
  // Egg / just-hatched seed for stage 1, full creature from stage 2.
  const conf = { 2: { ry: 42, gap: 15, eye: 8 }, 3: { ry: 47, gap: 17, eye: 9 }, 4: { ry: 51, gap: 19, eye: 10 }, 5: { ry: 55, gap: 20, eye: 11 } }[stage];

  const bob = dormant
    ? { scale: [1, 1.025, 1], rotate: 0 }
    : { y: [0, -6, 0], rotate: [-1, 1.5, -1] };
  const bobT = { duration: dormant ? 4.2 : 3.4, repeat: Infinity, ease: 'easeInOut' };

  return (
    <div className={className} data-testid="metaphor-creature" data-stage={stage} data-dormant={dormant ? '1' : '0'}>
      <svg viewBox="0 0 240 280" width="100%" role="img" aria-label={`Companion creature, stage ${stage} of 5`}>
        <defs>
          <radialGradient id="pipBody" cx="42%" cy="34%" r="75%">
            <stop offset="0%" stopColor="#67B89B" />
            <stop offset="100%" stopColor={BODY} />
          </radialGradient>
          <radialGradient id="pipGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#F3E2B4" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#F3E2B4" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* ground + shadow */}
        <ellipse cx="120" cy="256" rx="60" ry="8" fill="#D7DBD3" />

        <motion.g animate={bob} transition={bobT} style={{ transformOrigin: '120px 250px' }}>
          {stage === 1 ? (
            /* ---- Stage 1: just-hatched seed ---- */
            <g>
              <ellipse cx="120" cy="248" rx="24" ry="7" fill="#C7CEC6" />
              <path d="M120 218 C 98 218 92 246 120 250 C 148 246 142 218 120 218 Z" fill="url(#pipBody)" stroke={EDGE} strokeOpacity="0.2" />
              <ellipse cx="120" cy="240" rx="15" ry="9" fill={BELLY} opacity="0.85" />
              {/* peeking eyes */}
              {dormant ? (
                <>
                  <path d="M111 231 q 3 3 6 0" stroke={INK} strokeWidth="1.8" fill="none" strokeLinecap="round" />
                  <path d="M123 231 q 3 3 6 0" stroke={INK} strokeWidth="1.8" fill="none" strokeLinecap="round" />
                </>
              ) : (
                <>
                  <circle cx="114" cy="231" r="2.4" fill={INK} />
                  <circle cx="126" cy="231" r="2.4" fill={INK} />
                </>
              )}
              <path d="M116 238 q 4 3 8 0" stroke={INK} strokeWidth="1.6" fill="none" strokeLinecap="round" />
              {/* sprout */}
              <path d="M120 218 L120 206" stroke={SPROUT} strokeWidth="2.6" strokeLinecap="round" />
              <HeadLeaf x={120} y={206} rot={-22} len={12} />
              <HeadLeaf x={120} y={206} rot={22} len={12} />
            </g>
          ) : (
            /* ---- Stage 2+: full creature ---- */
            (() => {
              const { ry, gap, eye } = conf;
              const rx = ry * 0.9;
              const cy = 246 - ry;
              const topY = cy - ry;
              const eyeY = cy - ry * 0.1;
              const mouthY = cy + ry * 0.2;
              return (
                <g>
                  {/* feet */}
                  <ellipse cx={120 - 15} cy="244" rx="10" ry="6" fill={FOOT} />
                  <ellipse cx={120 + 15} cy="244" rx="10" ry="6" fill={FOOT} />

                  {/* tail — stage 4+ */}
                  {stage >= 4 && (
                    <path d={`M${120 + rx - 4},${cy + 6} q 26,4 20,-22 q -3,-12 -14,-8`} fill="none" stroke={BODY} strokeWidth="7" strokeLinecap="round" />
                  )}

                  {/* ear tufts — stage 3+ */}
                  {stage >= 3 && (
                    <>
                      <HeadLeaf x={120 - rx * 0.78} y={cy - ry * 0.5} rot={-58} len={18} fill={BODY} />
                      <HeadLeaf x={120 + rx * 0.78} y={cy - ry * 0.5} rot={58} len={18} fill={BODY} />
                    </>
                  )}

                  {/* body */}
                  <ellipse cx="120" cy={cy} rx={rx} ry={ry} fill="url(#pipBody)" stroke={EDGE} strokeOpacity="0.18" />
                  <ellipse cx="120" cy={cy + ry * 0.2} rx={rx * 0.64} ry={ry * 0.58} fill={BELLY} opacity="0.9" />

                  {/* belly freckles — stage 4+ */}
                  {stage >= 4 && (
                    <g fill={BODY} opacity="0.5">
                      <circle cx="112" cy={cy + ry * 0.28} r="1.8" />
                      <circle cx="126" cy={cy + ry * 0.34} r="1.8" />
                      <circle cx="120" cy={cy + ry * 0.12} r="1.6" />
                    </g>
                  )}

                  {/* arms — stage 3+ */}
                  {stage >= 3 && (
                    <>
                      <path d={`M${120 - rx + 4},${cy + 2} q -12,4 -10,16`} fill="none" stroke={BODY} strokeWidth="7" strokeLinecap="round" />
                      <path d={`M${120 + rx - 4},${cy + 2} q 12,4 10,16`} fill="none" stroke={BODY} strokeWidth="7" strokeLinecap="round" />
                    </>
                  )}

                  {/* mane collar — stage 5 */}
                  {stage >= 5 && (
                    <g>
                      {[-46, -23, 0, 23, 46].map((a) => (
                        <HeadLeaf key={a} x={120} y={cy + ry * 0.52} rot={a + 180} len={13} fill={SPROUT} />
                      ))}
                    </g>
                  )}

                  {/* cheeks — stage 3+ */}
                  {stage >= 3 && (
                    <g fill={CHEEK} opacity="0.55">
                      <ellipse cx={120 - gap - eye} cy={eyeY + eye + 2} rx="5.5" ry="3.6" />
                      <ellipse cx={120 + gap + eye} cy={eyeY + eye + 2} rx="5.5" ry="3.6" />
                    </g>
                  )}

                  {/* brows — stage 4+ (a touch of mischief) */}
                  {stage >= 4 && !dormant && (
                    <>
                      <path d={`M${120 - gap - eye},${eyeY - eye - 3} q ${eye},-3 ${eye * 1.6},1`} stroke={INK} strokeWidth="1.6" fill="none" strokeLinecap="round" />
                      <path d={`M${120 + gap - eye * 0.6},${eyeY - eye - 4} q ${eye},-3 ${eye * 1.6},2`} stroke={INK} strokeWidth="1.6" fill="none" strokeLinecap="round" />
                    </>
                  )}

                  {/* eyes */}
                  {dormant ? (
                    <>
                      <path d={`M${120 - gap - eye * 0.7},${eyeY} q ${eye * 0.7},${eye * 0.7} ${eye * 1.4},0`} stroke={INK} strokeWidth="2.2" fill="none" strokeLinecap="round" />
                      <path d={`M${120 + gap - eye * 0.7},${eyeY} q ${eye * 0.7},${eye * 0.7} ${eye * 1.4},0`} stroke={INK} strokeWidth="2.2" fill="none" strokeLinecap="round" />
                    </>
                  ) : (
                    <>
                      {[-1, 1].map((d) => (
                        <g key={d}>
                          <ellipse cx={120 + d * gap} cy={eyeY} rx={eye} ry={eye * 1.12} fill="#fff" />
                          <circle cx={120 + d * gap + (stage >= 4 ? d * 1.5 : 0)} cy={eyeY + eye * 0.25} r={eye * 0.52} fill={INK} />
                          <circle cx={120 + d * gap - 1.5} cy={eyeY - eye * 0.25} r={eye * 0.2} fill="#fff" />
                        </g>
                      ))}
                    </>
                  )}

                  {/* mouth */}
                  {dormant ? (
                    <path d={`M${120 - 5},${mouthY} q 5,4 10,0`} stroke={INK} strokeWidth="1.8" fill="none" strokeLinecap="round" />
                  ) : stage >= 4 ? (
                    <g>
                      <path d={`M${120 - 9},${mouthY} q 9,10 18,0 z`} fill="#8A3A4A" />
                      <path d={`M${120 - 4},${mouthY + 4} a 4 4 0 0 0 8 0`} fill={CHEEK} />
                    </g>
                  ) : (
                    <path d={`M${120 - 8},${mouthY} q 8,8 16,0`} stroke={INK} strokeWidth="2" fill="none" strokeLinecap="round" />
                  )}

                  {/* head sprout — grows by stage */}
                  <path d={`M120,${topY + 2} L120,${topY - 10}`} stroke={SPROUT} strokeWidth="2.8" strokeLinecap="round" />
                  {stage === 2 && <HeadLeaf x={120} y={topY - 10} rot={-16} len={14} />}
                  {stage === 3 && (
                    <>
                      <HeadLeaf x={120} y={topY - 10} rot={-26} len={14} />
                      <HeadLeaf x={120} y={topY - 10} rot={26} len={14} />
                    </>
                  )}
                  {stage >= 4 && (
                    <>
                      <HeadLeaf x={120} y={topY - 8} rot={-34} len={13} />
                      <HeadLeaf x={120} y={topY - 12} rot={0} len={15} />
                      <HeadLeaf x={120} y={topY - 8} rot={34} len={13} />
                    </>
                  )}
                  {stage >= 5 && (
                    <>
                      <circle cx="120" cy={topY - 20} r="16" fill="url(#pipGlow)" />
                      {[0, 72, 144, 216, 288].map((a) => (
                        <ellipse key={a} cx="120" cy={topY - 26} rx="3.4" ry="6" fill="#E7C9D6" transform={`rotate(${a} 120 ${topY - 20})`} />
                      ))}
                      <circle cx="120" cy={topY - 20} r="3" fill={C.gold} />
                    </>
                  )}

                  {/* sparkles — stage 5 */}
                  {stage >= 5 && !dormant && (
                    <g fill={C.gold}>
                      <motion.circle cx="78" cy={cy - 10} r="2" animate={{ opacity: [0.2, 1, 0.2] }} transition={{ duration: 2.2, repeat: Infinity }} />
                      <motion.circle cx="166" cy={cy + 6} r="2.4" animate={{ opacity: [1, 0.2, 1] }} transition={{ duration: 2.6, repeat: Infinity }} />
                    </g>
                  )}
                </g>
              );
            })()
          )}

          {/* resting z's */}
          {dormant && (
            <g fill={C.inkSoft} fontFamily="Fraunces, serif">
              <motion.text x="158" y="150" fontSize="14" animate={{ opacity: [0, 1, 0], y: [150, 136, 150] }} transition={{ duration: 3, repeat: Infinity }}>z</motion.text>
              <motion.text x="168" y="138" fontSize="18" animate={{ opacity: [0, 1, 0], y: [138, 120, 138] }} transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}>Z</motion.text>
            </g>
          )}
        </motion.g>
      </svg>
    </div>
  );
}
